import { create } from 'zustand';

export type RailPageKey =
  | 'home'
  | 'explore'
  | 'profile-own'
  | 'profile-other'
  | 'post-detail'
  | 'communities'
  | 'community-detail'
  | 'bookmarks'
  | 'notifications'
  | 'messages'
  | 'videos'
  | 'video-watch'
  | 'live'
  | 'premium'
  | 'feedback'
  | 'feedback-detail'
  | 'feedback-roadmap';
export interface RailLayout {
  order: string[];
  hidden: string[];
}
type Layouts = Partial<Record<RailPageKey, RailLayout>>;
const storageKey = (owner: string) => `truxsocial:rail-layouts:${owner}`;

export function readRailLayouts(owner: string): Layouts {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey(owner)) ?? '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, entry]) => {
        if (!entry || !Array.isArray(entry.order) || !Array.isArray(entry.hidden)) return [];
        const strings = (xs: unknown[]) => [
          ...new Set(xs.filter((x): x is string => typeof x === 'string')),
        ];
        return [[key, { order: strings(entry.order), hidden: strings(entry.hidden) }]];
      }),
    );
  } catch {
    return {};
  }
}

interface State {
  accounts: Record<string, Layouts>;
  save: (owner: string, page: RailPageKey, layout: RailLayout | null) => void;
}
export const useRailLayoutsStore = create<State>()((set, get) => ({
  accounts: {},
  save(owner, page, layout) {
    const pages = { ...(get().accounts[owner] ?? readRailLayouts(owner)) };
    if (layout) pages[page] = layout;
    else delete pages[page];
    set((state) => ({ accounts: { ...state.accounts, [owner]: pages } }));
    try {
      localStorage.setItem(storageKey(owner), JSON.stringify(pages));
    } catch {
      /* Session-only when storage is unavailable. */
    }
  },
}));

export function resolveRailLayout(
  panels: readonly { id: string; required?: boolean; defaultHidden?: boolean }[],
  saved?: RailLayout,
): RailLayout {
  const available = new Map(panels.map((panel) => [panel.id, panel]));
  const order = [...new Set([...(saved?.order ?? []), ...available.keys()])].filter((id) =>
    available.has(id),
  );
  const defaultHidden = panels
    .filter((panel) => panel.defaultHidden && !saved?.order.includes(panel.id))
    .map((panel) => panel.id);
  const hidden = [...new Set([...(saved?.hidden ?? []), ...defaultHidden])].filter(
    (id) => available.has(id) && !available.get(id)?.required,
  );
  return { order, hidden };
}
